import {
  Check as CheckIcon,
  FirstPage as FirstPageIcon,
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  LastPage as LastPageIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import DropdownCell from './DropdownCell'
import ExpandCircleDownOutlinedIcon from '@mui/icons-material/ExpandCircleDownOutlined'
import {
  Box,
  Checkbox,
  FormControl,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import EntityEmpty from '../../assets/icons/EntityEmpty'
import { GLOBAL_CONSTANTS } from '../../constants/globalConstants'
import { useSidebar } from '../../contexts/SidebarContext'
import { TOTAL_KEY_MAPPING } from '../../constants/prepare'
import { formatIndianNumber } from '../../constants/index.js'
import React from 'react'
import EditIcon from '../../assets/icons/EditIcon.jsx'
import DeleteIcon from '../../assets/icons/DeleteIcon.jsx'

const EllipsisWithTooltip = ({ value }) => {
  const spanRef = React.useRef(null)
  const [isOverflowed, setIsOverflowed] = React.useState(false)

  React.useEffect(() => {
    const el = spanRef.current
    if (!el) return

    const checkOverflow = () => setIsOverflowed(el.scrollWidth > el.clientWidth)
    checkOverflow()
    window.addEventListener('resize', checkOverflow)
    return () => window.removeEventListener('resize', checkOverflow)
  }, [value])

  return (
    <Tooltip title={isOverflowed ? value : ''} arrow>
      <div
        ref={spanRef}
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          cursor: isOverflowed ? 'pointer' : 'default',
          width: '100%',
        }}
      >
        {value}
      </div>
    </Tooltip>
  )
}

const TableSkeleton = ({ rows = 5, columns = 5, showCheckbox = false }) => {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <TableRow key={rowIndex}>
          {showCheckbox && (
            <TableCell
              sx={{
                borderRight: '1px solid var(--border-subtel)',
                borderBottom: '1px solid var(--border-subtel)',
                width: '50px',
                minWidth: '50px',
                maxWidth: '50px',
                padding: '12px 17px',
                textAlign: 'right',
              }}
            >
              <Skeleton variant="rectangular" width={15} height={15} />
            </TableCell>
          )}
          {Array.from({ length: columns }).map((_, colIndex) => (
            <TableCell
              key={colIndex}
              sx={{
                borderRight:
                  colIndex < columns - 1 ? '1px solid var(--border-subtel)' : 'none',
                borderBottom: '1px solid var(--border-subtel)',
                width: colIndex === columns - 1 ? '120px' : '200px',
                minWidth: colIndex === columns - 1 ? '120px' : '150px',
                maxWidth: colIndex === columns - 1 ? '120px' : 'none',
              }}
            >
              <Skeleton variant="text" width="100%" height={20} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}

const TableFooter = ({
  totalItems = 100,
  currentPage = 1,
  itemsPerPage = 10,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [10, 25, 50, 100],
  showItemsPerPage = true,
  showTotalItems = true,
  showPageNumbers = true,
  customFooterContent,
  selectedCount = 0,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalItems)

  const handleFirstPage = () => {
    if (currentPage > 1 && onPageChange) {
      onPageChange(1)
    }
  }

  const handlePreviousPage = () => {
    if (currentPage > 1 && onPageChange) {
      onPageChange(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages && onPageChange) {
      onPageChange(currentPage + 1)
    }
  }

  const handleLastPage = () => {
    if (currentPage < totalPages && onPageChange) {
      onPageChange(totalPages)
    }
  }

  const handleItemsPerPageChange = event => {
    const newItemsPerPage = event.target.value
    if (onItemsPerPageChange) {
      onItemsPerPageChange(newItemsPerPage)
    }
  }

  if (customFooterContent) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderTop: '1px solid var(--border-subtel)',
          backgroundColor: '#fafafa',
          // minHeight: '56px',
        }}
      >
        {customFooterContent}
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'end',
        padding: '4px',
        borderTop: '1px solid var(--border-subtel)',
        backgroundColor: '#fafafa',
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {selectedCount > 0 && (
          <Typography variant="body2" color="var(--forbrand-color)" sx={{ fontWeight: 500 }}>
            {selectedCount} item{selectedCount > 1 ? 's' : ''} selected
          </Typography>
        )}

        {showItemsPerPage && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Items per page:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 70 }}>
              <Select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                sx={{
                  fontSize: '14px',
                  '& .MuiSelect-select': {
                    padding: '4px 8px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 0,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 0,
                  },
                }}
              >
                {itemsPerPageOptions.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {showTotalItems && (
          <Typography variant="body2" sx={{ color: '#CF6210' }}>
            {totalItems === 0 ? '0 of 0' : `${endItem} of ${totalItems}`}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {showPageNumbers && totalPages > 1 && (
          <>
            <Tooltip title={GLOBAL_CONSTANTS.FIRST_PAGE}>
              <IconButton
                size="small"
                onClick={handleFirstPage}
                disabled={currentPage === 1}
                sx={{
                  color: currentPage === 1 ? '#ccc' : '#666',
                  '&:hover': {
                    backgroundColor:
                      currentPage === 1 ? 'transparent' : 'var(--border-subtel)',
                  },
                }}
              >
                <FirstPageIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={GLOBAL_CONSTANTS.PREVIOUS_PAGE}>
              <IconButton
                size="small"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                sx={{
                  color: currentPage === 1 ? '#ccc' : '#666',
                  '&:hover': {
                    backgroundColor:
                      currentPage === 1 ? 'transparent' : 'var(--border-subtel)',
                  },
                }}
              >
                <KeyboardArrowLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mx: 1 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(event, page) => onPageChange && onPageChange(page)}
                size="small"
                shape="rounded"
                showFirstButton={false}
                showLastButton={false}
                siblingCount={1}
                boundaryCount={1}
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: '12px',
                    minWidth: '28px',
                    height: '22px',
                  },
                }}
              />
            </Box>

            <Tooltip title={GLOBAL_CONSTANTS.NEXT_PAGE}>
              <IconButton
                size="small"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                sx={{
                  color: currentPage === totalPages ? '#ccc' : '#666',
                  '&:hover': {
                    backgroundColor:
                      currentPage === totalPages ? 'transparent' : 'var(--border-subtel)',
                  },
                }}
              >
                <KeyboardArrowRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={GLOBAL_CONSTANTS.LAST_PAGE}>
              <IconButton
                size="small"
                onClick={handleLastPage}
                disabled={currentPage === totalPages}
                sx={{
                  color: currentPage === totalPages ? '#ccc' : '#666',
                  '&:hover': {
                    backgroundColor:
                      currentPage === totalPages ? 'transparent' : 'var(--border-subtel)',
                  },
                }}
              >
                <LastPageIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  )
}

const CommonTable = ({
  columns = [],
  tabStatus = '',
  data = [],
  loading = false,
  onEdit,
  disabledEdit = false,
  currentTable = '',
  onDelete,
  onRowClick,
  showActions = true,
  showEdit = true,
  emptyMessage = 'No business entity added yet',
  emptyIcon = <EntityEmpty />,
  skeletonRows = 10,
  // Footer/Pagination props
  showFooter = false,
  totalItems = 100,
  currentPage = 1,
  itemsPerPage = 10,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [10, 25, 50, 100],
  showItemsPerPage = true,
  showTotalItems = true,
  showPageNumbers = true,
  customFooterContent,
  stickyFooter = true,
  // When true (default), the container min-height constraints apply.
  // Set to false to let the table size naturally with no height clamping.
  applyMinHeightConstraints = true,
  // Checkbox selection props
  showCheckbox = false,
  selectedItems = [],
  onSelectItem,
  onSelectAll,
  // Edit mode props
  editMode = false,
  editingData = {},
  onFieldChange,
  onSaveEdit,
  // Row expansion props
  expandedRows = new Set(),
  onRowExpand,
  showExpandColumn = false,
  totalSumData = {},
}) => {
  const TOTAL_COLUMNS = Object.keys(TOTAL_KEY_MAPPING)

  function buildTotalRow(totalSumData, data) {
    const totalRow = {}

    const VALID_TABS = [
      'unprocessed',
      'processed',
      'exceptions',
      'excluded',
      'unmapped',
      'mapped',
      'adjustments',
      'compute',
      'allocation'
    ]

    if (VALID_TABS.includes(tabStatus)) {
      TOTAL_COLUMNS.forEach(colId => {
        const totalKey = TOTAL_KEY_MAPPING[colId]

        if (
          totalSumData &&
          totalKey &&
          Object.prototype.hasOwnProperty.call(totalSumData, totalKey)
        ) {
          totalRow[colId] = totalSumData[totalKey] ?? 0
        } else {
          totalRow[colId] = data.reduce(
            (sum, row) => sum + (Number(row[colId]) || 0),
            0
          )
        }
      })
    }

    return totalRow
  }
  const totalRow = buildTotalRow(totalSumData, data)

  // Helper function to flatten columns and get all leaf columns
  const getFlatColumns = cols => {
    const flatColumns = []
    cols.forEach(col => {
      if (col.type === 'group' && col.columns) {
        flatColumns.push(...col.columns)
      } else {
        flatColumns.push(col)
      }
    })
    return flatColumns
  }

  // Helper function to get total column span including groups
  const getTotalColumnSpan = cols => {
    let span = 0
    cols.forEach(col => {
      if (col.type === 'group' && col.columns) {
        span += col.columns.length
      } else {
        span += 1
      }
    })
    return span
  }

  // Function to get all rows including expanded children
  const getAllRows = () => {
    const allRows = []

    data.forEach(row => {
      // Add parent row
      allRows.push(row)

      // Add children immediately after parent if row is expanded
      if (
        showExpandColumn &&
        row.hasChildren &&
        expandedRows.has(row.uuid) &&
        row.children
      ) {
        row.children.forEach(child => {
          allRows.push({ ...child, isChild: true, parentId: row.uuid })
        })
      }
    })

    return allRows
  }

  const allRowsToRender = getAllRows()
  const flatColumns = getFlatColumns(columns)
  const columnsCount =
    getTotalColumnSpan(columns) +
    (showActions ? 1 : 0) +
    (showCheckbox ? 1 : 0) +
    (showExpandColumn ? 1 : 0)
  const { isCollapsed } = useSidebar()

  const isAllSelected = data.length > 0 && selectedItems?.length === data.length
  const isIndeterminate =
    selectedItems.length > 0 && selectedItems.length < data.length

  const handleSelectAll = event => {
    if (onSelectAll) {
      onSelectAll(event)
    }
  }

  const handleSelectItem = itemId => {
    if (onSelectItem) {
      onSelectItem(itemId)
    }
  }

  // const renderCellContent = ({ row, col, isEditing }) => {
  //   let value = editingData[row.uuid]?.[col.id] ?? row[col.id] ?? '';
  //   if (col.type==='dropdown') {
  //     const sel = col.options.find(o=>o.value===value);
  //     value = sel ? sel.label : value;
  //   }
  //   if (typeof col.render === "function") {
  //     if (col.render.length === 1) return col.render(value);
  //     return col.render(value, row);
  //   }
  //   if (isEditing && col.editable) {
  //     if (col.type==='dropdown') return <DropdownCell value={value} options={col.options} onChange={val=>onFieldChange && onFieldChange(row.uuid,col.id,val)}/>;
  //     if (col.type==='date') return <input type="date" value={value?value.split('/').reverse().join('-'):''} onChange={e=>onFieldChange && onFieldChange(row.uuid,col.id,e.target.value?e.target.value.split('-').reverse().join('/'):'' )} style={{width:'100%'}}/>;
  //     return <input type="text" value={value} onChange={e=>onFieldChange && onFieldChange(row.uuid,col.id,e.target.value)} style={{width:'100%'}}/>;
  //   }
  //   return <EllipsisWithTooltip value={value}/>;
  // };

  const renderCellContent = ({ row, col, isEditing }) => {
    const isColumnEditable =
      typeof col.editable === 'function'
        ? col.editable(row)
        : col.editable
    let value = editingData[row.uuid]?.[col.id] ?? row[col.id] ?? ''
    const rawValue = value
    if (col.type === 'dropdown') {
      const sel = col.options.find(o => o.value === value || String(o.value) === String(value))
      value = sel ? sel.label : value
    }

    if (typeof col.render === 'function') {
      if (col.render.length === 1) return col.render(value)
      return col.render(value, row)
    }

    // Format number
    let formatValue
    if (typeof value === 'boolean') {
      formatValue = value ? 'True' : 'False'
    } else if (col.id === 'sbtRuleBasedSection') {
      formatValue = value
        .split(/\s*,\s*/)
        .filter(Boolean)
        .join(', ')
    } else if (value == 'Select') {
      formatValue = value == 'Select' && ''
    } else {
      formatValue = formatIndianNumber(value, col.isFormatted)
    }
    if (isEditing && isColumnEditable) {
      if (col.type === 'dropdown') {
        return (
          <DropdownCell
            value={rawValue}
            options={col.options}
            onChange={val =>
              onFieldChange && onFieldChange(row.uuid, col.id, val)
            }
          />
        )
      }
      if (col.type === 'date') {
        return (
          <input
            type="date"
            value={value ? value.split('/').reverse().join('-') : ''}
            onChange={e =>
              onFieldChange &&
              onFieldChange(
                row.uuid,
                col.id,
                e.target.value
                  ? e.target.value.split('-').reverse().join('/')
                  : ''
              )
            }
            style={{ width: '100%' }}
          />
        )
      }
      return (
        <input
          type="text"
          value={value}
          onChange={e => {
            // panOfDeductee must always be stored/displayed in uppercase,
            // whether the user types or pastes a value.
            const nextValue =
              col.id === 'panOfDeductee'
                ? e.target.value.toUpperCase()
                : e.target.value
            onFieldChange && onFieldChange(row.uuid, col.id, nextValue)
          }}
          style={{ width: '100%' }}
        />
      )
    }

    // Skip EllipsisWithTooltip for specific columns - show full text without truncation
    if (col.id === 'broadHead' || col.id === 'keyword' || col.id === 'sectionKeyword' || col.id === 'mappedTdsSection' || col.id === 'section' || col.id === 'finalReturnTdsSection' || col.id === 'tdsSection' || col.id === 'sectionAsPerChallan' || col.id === 'sbtRuleBasedSection') {
      return formatValue
    }

    // Preserve whitespace for Vendor Code (fu_01) and Duplicate Reference Number columns
    if (col.id === 'fu_01' || col.id === 'duplicateReferenceNumber') {
      const trimmedValue = formatValue ? formatValue.toString().trim() : ''
      return (
        <Tooltip title={<span style={{ whiteSpace: 'pre' }}>{trimmedValue}</span>} arrow>
          <div
            style={{
              whiteSpace: 'pre',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: '100%',
            }}
          >
            {trimmedValue}
          </div>
        </Tooltip>
      )
    }

    return <EllipsisWithTooltip value={formatValue} />
  }

  const isEmpty = !loading && allRowsToRender.length === 0

  const checkDisabledFlag = row => {
    if (currentTable === 'tableUsingIsActive') {
      return !row?.isActive
    } else if (currentTable === 'tableUsingRuleStatus') {
      return row?.ruleStatus?.toLowerCase() === 'inactive' || row?.defaultRule
    } else if (currentTable === 'allocation_page') {
      row?.allocationStatus !== 'New'
    }
    return false
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        maxHeight: showFooter ? 'calc(100vh - 250px)' : 'auto',
        minHeight: (applyMinHeightConstraints && showFooter && '300px') || 'auto'
      }}
    >
      <TableContainer
        component={Paper}
        className="hide-scrollbar"
        sx={{
          boxShadow: 'none',
          border: '1px solid var(--border-subtel)',
          borderBottom: showFooter && stickyFooter ? 'none' : '1px solid var(--border-subtel)',
          flex: 1,
          maxHeight: isEmpty ? 'none' : 'calc(100vh - 280px)',
          marginBottom: showFooter ? 0 : '28px',
          position: 'relative',
        }}
      >
        <Table
          stickyHeader
          sx={{
            tableLayout: 'fixed',
            minWidth: '100%',
            //  height: isEmpty ? '320px' : 'auto'
          }}
        >
          <TableHead sx={{ position: 'sticky', top: 0, zIndex: 10 }}>
            {/* Group Header Row */}
            <TableRow
              sx={{
                height: '28px !important',
                maxHeight: '28px',
                minHeight: '22px',
                '& > *': {
                  height: '28px !important',
                  maxHeight: '28px',
                  minHeight: '22px',
                },
              }}
            >
              {showCheckbox && !showExpandColumn && columns.length > 0 && (
                <TableCell
                  rowSpan={2}
                  sx={{
                    fontWeight: 600,
                    borderBottom: '1px solid var(--border-subtel)',
                    borderRight: '1px solid var(--border-subtel)',
                    background: 'var(--table-header-bg)',
                    padding: '4px 8px',
                    minHeight: '22px',
                    height: '22px',
                    borderTopLeftRadius: '8px',
                    width: '50px',
                    minWidth: '50px',
                    maxWidth: '50px',
                    fontSize: '14px',
                    lineHeight: '1.1',
                    textAlign: 'center',
                    color: '#fff',
                    verticalAlign: 'middle',
                  }}
                >
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={handleSelectAll}
                    sx={{
                      '&.Mui-checked': {
                        color: 'var(--forbrand-color)',
                      },
                      '&.MuiCheckbox-indeterminate': {
                        color: 'var(--forbrand-color)',
                      },
                      color: 'var(--forbrand-color)',
                      padding: 0,
                    }}
                    size="small"
                  />
                </TableCell>
              )}

              {showExpandColumn && (
                <TableCell
                  rowSpan={2}
                  sx={{
                    fontWeight: 600,
                    borderBottom: '1px solid var(--border-subtel)',
                    borderRight: '1px solid var(--border-subtel)',
                    background: 'var(--table-header-bg)',
                    padding: '4px 8px',
                    minHeight: '22px',
                    height: '22px',
                    width: '50px',
                    minWidth: '50px',
                    maxWidth: '50px',
                    fontSize: '14px',
                    lineHeight: '1.1',
                    textAlign: 'center',
                    color: '#fff',
                    verticalAlign: 'middle',
                  }}
                >
                  {/* Empty header for expand column */}
                </TableCell>
              )}

              {columns.map(column => {
                if (column.type === 'group') {
                  return (
                    <TableCell
                      key={column.id}
                      colSpan={column?.columns?.length}
                      sx={{
                        fontWeight: 600,
                        color: 'var(--forbrand-primary)',
                        borderBottom: '1px solid var(--border-subtel)',
                        borderRight: '1px solid var(--border-subtel)',
                        background: column?.background || 'var(--table-header-bg)',
                        padding: '12px',
                        minHeight: '40px',
                        fontSize: '14px',
                        lineHeight: '16px',
                        textAlign: 'center',
                        width: column.width || '200px',
                      }}
                    >
                      {column.label}
                    </TableCell>
                  )
                } else {
                  return (
                    <TableCell
                      key={column.id}
                      rowSpan={2}
                      sx={{
                        color: 'var(--forbrand-primary)',
                        borderBottom: '1px solid var(--border-subtel)',
                        borderRight: '1px solid var(--border-subtel)',
                        background: column?.background || 'var(--table-header-bg)',
                        // padding: '4px 12px',
                        minHeight: '22px',
                        height: '22px',
                        width: column.width || '200px',
                        minWidth: column.minWidth || '150px',
                        maxWidth: column.maxWidth || 'none',
                        verticalAlign: 'middle',
                        textAlign: 'left',
                        // ...(column.align && { textAlign: column.align }),
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent:
                            column.align === 'left'
                              ? 'flex-start'
                              : column.align === 'right'
                                ? 'flex-end'
                                : 'flex-start',
                          // height: '20px',
                          width: '100%',
                          maxWidth: column.maxWidth || 'none',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          lineHeight: '1.1',
                          fontSize: '14px',
                          fontWeight: 600,
                          textAlign: column.align || 'center',
                        }}
                      >
                        {column.label}
                      </Box>
                    </TableCell>
                  )
                }
              })}

              {showActions && columns.length > 0 && (
                <TableCell
                  rowSpan={2}
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    color: 'var(--forbrand-primary)',
                    background: 'var(--table-header-bg)',
                    borderBottom: '1px solid var(--border-subtel)',
                    borderTopRightRadius: '8px',
                    padding: '4px 12px',
                    textAlign: 'center',
                    width: '100px',
                    minWidth: '120px',
                    maxWidth: '120px',
                    minHeight: '22px',
                    height: '22px',
                    verticalAlign: 'middle',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '20px',
                      width: '100%',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }}
                  >
                    Actions
                  </Box>
                </TableCell>
              )}
            </TableRow>

            {/* Sub-header Row for grouped columns */}
            <TableRow
              sx={{
                height: '28px !important',
                maxHeight: '28px',
                minHeight: '22px',
                '& > *': {
                  height: '28px !important',
                  maxHeight: '28px',
                  minHeight: '22px',
                },
              }}
            >
              {columns.map(column => {
                if (column.type === 'group') {
                  return column.columns.map(subColumn => (
                    <TableCell
                      key={`${column.id}-${subColumn.id}`}
                      rowSpan={2}
                      sx={{
                        fontWeight: 600,
                        color: 'var(--forbrand-primary)',
                        borderBottom: '2px solid var(--border-subtel)',
                        borderRight: '1px solid var(--border-subtel)',
                        background: column?.background || 'var(--table-header-bg)',
                        padding: '4px 12px',
                        minHeight: '22px',
                        height: '22px',
                        width: subColumn.width || '150px',
                        minWidth: subColumn.minWidth || '100px',
                        maxWidth: subColumn.maxWidth || 'none',
                        fontSize: '14px',
                        lineHeight: '1.1',
                        verticalAlign: 'middle',
                        ...(subColumn.align && { textAlign: subColumn.align }),
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent:
                            subColumn.align === 'left'
                              ? 'flex-start'
                              : subColumn.align === 'right'
                                ? 'flex-end'
                                : 'center',
                          height: '20px',
                          width: '100%',
                          maxWidth: '250px',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          lineHeight: '1.1',
                          fontSize: '14px',
                          textAlign: subColumn.align || 'center',
                        }}
                      >
                        {subColumn.label}
                      </Box>
                    </TableCell>
                  ))
                }
                return null // Non-group columns are already rendered with rowSpan=2
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableSkeleton
                rows={skeletonRows}
                columns={getTotalColumnSpan(columns)}
                showCheckbox={showCheckbox}
              />
            ) : isEmpty ? (
              <TableRow>
                <TableCell
                  colSpan={columnsCount}
                  sx={{
                    borderBottom: 'none',
                    padding: 0,
                    background: 'transparent',
                    height: '320px',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      minHeight: '320px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      zIndex: 2,
                    }}
                  >
                    {emptyIcon}
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        textAlign: 'center',
                        width: '100%',
                        fontSize: '18px',
                        fontWeight: 500,
                        mt: 2,
                      }}
                    >
                      {emptyMessage}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {Object.keys(totalRow).length > 0 && (
                  <TableRow
                    sx={{
                      fontWeight: 600,
                      '& td': {
                        borderBottom: '1px solid var(--border-subtel)',
                        color: 'var(--forbrand-primary)',
                        padding: '12px 16px',
                        whiteSpace: 'normal',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        wordBreak: 'break-word',
                      },
                    }}
                  >
                    {showCheckbox && <TableCell />}
                    {flatColumns.map((col, colIndex) => (
                      <TableCell
                        key={col.id}
                        sx={{
                          width: col.width || '200px',
                          minWidth: col.minWidth || '150px',
                          maxWidth: col.maxWidth || 'none',
                          borderRight:
                            colIndex < flatColumns.length - 1
                              ? '1px solid var(--border-subtel)'
                              : 'none',
                          textAlign: col.align || 'left',
                          fontWeight: '700',
                        }}
                      >
                        {totalRow[col.id] !== undefined
                          ? `${formatIndianNumber(totalRow[col.id])}`
                          : ''}
                      </TableCell>
                    ))}
                    {showActions && columns.length > 0 && <TableCell />}
                  </TableRow>
                )}

                {allRowsToRender.map((row, index) => {
                  const isSelected = selectedItems?.includes(row.uuid)
                  const isExpanded = expandedRows.has(row.uuid)
                  const isCellEditable = (row, column) => {
                    if (typeof column.editable === 'function') {
                      return column.editable(row)
                    }
                    return !!column.editable
                  }


                  return (
                    <TableRow
                      key={
                        row.isChild ? `child-${row.uuid}` : row.uuid || index
                      }
                      onClick={
                        onRowClick && !row.isChild
                          ? () => onRowClick(row)
                          : undefined
                      }

                      sx={{
                        cursor: onRowClick && !row.isChild ? 'pointer' : 'default',
                        // '&:hover': {
                        //   backgroundColor: '#f8f9fa',
                        // },
                        // '&:nth-of-type(even)': {
                        //   backgroundColor: isSelected ? '#fff3f0' : '#fafafa',
                        // },
                        // backgroundColor: isSelected ? '#fff3f0' : 'transparent',
                        // Add left border for child rows
                        ...(row.isChild && {
                          borderLeft: '3px solid #ff6b35',
                          backgroundColor: '#fff9f7',
                        }),

                      }}
                    >
                      {showCheckbox &&
                        !showExpandColumn &&
                        columns.length > 0 && (
                          <TableCell
                            onClick={e => e.stopPropagation()}
                            sx={{
                              padding: '12px 8px',
                              borderBottom: '1px solid var(--border-subtel)',
                              color: 'var(--forbrand-primary)',
                              borderRight: '1px solid var(--border-subtel)',
                              width: '50px',
                              minWidth: '50px',
                              maxWidth: '50px',
                              textAlign: 'center',
                            }}
                          >
                            {!row.isChild && (
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleSelectItem(row.uuid)}
                                sx={{
                                  '&.Mui-checked': {
                                    color: 'var(--forbrand-color)',
                                  },
                                  color: 'var(--forbrand-color)',
                                  padding: 0,
                                }}
                                size="small"
                                disabled={!row?.fileName && (row?.fileSource === 'Reload' || row?.fileSource === 'REPROCESSED_DATA' || row?.fileSource === 'Reprocessed Data')}
                              />
                            )}
                          </TableCell>
                        )}

                      {showExpandColumn && (
                        <TableCell
                          sx={{
                            padding: '12px 8px',
                            borderBottom: '1px solid var(--border-subtel)',
                            borderRight: '1px solid var(--border-subtel)',
                            color: 'var(--forbrand-primary)',
                            width: '50px',
                            minWidth: '50px',
                            maxWidth: '50px',
                            textAlign: 'center',
                          }}
                        >
                          {!row.isChild && row.hasChildren && (
                            <IconButton
                              size="small"
                              onClick={() =>
                                onRowExpand && onRowExpand(row.uuid)
                              }
                              sx={{
                                color: 'var(--forbrand-color)',
                                padding: 0,
                              }}
                            >
                              {isExpanded ? (
                                <ExpandCircleDownOutlinedIcon
                                  sx={{ width: '18px', height: '18px' }}
                                />
                              ) : (
                                <ExpandCircleDownOutlinedIcon
                                  sx={{ width: '18px', height: '18px' }}
                                />
                              )}
                            </IconButton>
                          )}
                        </TableCell>
                      )}

                      {flatColumns.map((column, colIndex) => {
                        const editable = isCellEditable(row, column)
                        const isNoWrapColumn = column.id === 'broadHead' || column.id === 'keyword' || column.id === 'sectionKeyword' || column.id === 'mappedTdsSection' || column.id === 'section' || column.id === 'finalReturnTdsSection' || column.id === 'tdsSection' || column.id === 'sectionAsPerChallan' || column.id === 'sbtRuleBasedSection'

                        return (
                          <TableCell
                            key={column.id}
                            sx={{
                              padding: '12px 16px',
                              borderBottom: '1px solid var(--border-subtel)',
                              borderRight:
                                colIndex < flatColumns.length - 1 || showActions
                                  ? '1px solid var(--border-subtel)'
                                  : 'none',
                              width: column.width || '200px',
                              minWidth: column.minWidth || '150px',
                              maxWidth: column.maxWidth || 'none',
                              overflow: isNoWrapColumn ? 'visible' : 'hidden',
                              textOverflow: isNoWrapColumn ? 'clip' : 'ellipsis',
                              whiteSpace: isNoWrapColumn ? 'normal' : 'nowrap',
                              wordWrap: isNoWrapColumn ? 'break-word' : 'normal',
                              ...(column.align && { textAlign: column.align }),

                              ...(editMode &&
                                !editable && {
                                backgroundColor: '#f2f2f2',
                                color: '#9e9e9e',
                              }),

                              '.MuiTextField-root': {
                                width: '100%',
                              },
                            }}
                          >
                            {renderCellContent({
                              row,
                              col: column,
                              isEditing: editMode,
                              editingData,
                              handleEditChange: onFieldChange,
                            })}
                          </TableCell>
                        )
                      })}


                      {showActions && columns.length > 0 && (
                        <TableCell
                          sx={{
                            padding: '12px 16px',
                            borderBottom: '1px solid var(--border-subtel)',
                            color: 'var(--forbrand-primary)',
                            textAlign: 'center',
                            width: '120px',
                            minWidth: '120px',
                            maxWidth: '120px',
                          }}
                        >
                          {!row.isChild && (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: 1,
                                width: '100%',
                              }}
                            >
                              {editMode && selectedItems.includes(row.uuid) ? (
                                <Tooltip title={GLOBAL_CONSTANTS.SAVE}>
                                  <IconButton
                                    size="small"
                                    onClick={() => onSaveEdit && onSaveEdit()}
                                    sx={{
                                      color: '#4caf50',
                                      '&:hover': {
                                        backgroundColor: '#e8f5e8',
                                      },
                                    }}
                                  >
                                    <CheckIcon fontSize="medium" />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                onEdit &&
                                showEdit && (
                                  <Tooltip title={GLOBAL_CONSTANTS.EDIT}>
                                    <IconButton
                                      size="small"
                                      disabled={
                                        disabledEdit
                                          ? checkDisabledFlag(row)
                                          : false
                                      }
                                      onClick={() => onEdit(row)}
                                      sx={{
                                        color: '#000000',
                                        '&:hover': {
                                          backgroundColor: '#e3f2fd',
                                        },
                                      }}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                )
                              )}

                              {onDelete && !editMode && (
                                <Tooltip title={GLOBAL_CONSTANTS.DELETE}>
                                  <IconButton
                                    size="small"
                                    onClick={() => onDelete(row)}
                                    sx={{
                                      color: 'var( --forbrand-error)',
                                      '&:hover': {
                                        backgroundColor: '#ffebee',
                                      },
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  )
                })}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {showFooter && totalItems ? (
        <Paper
          sx={{
            border: '1px solid var(--border-subtel)',
            borderTop: 'none',
            boxShadow: 'none',
            position: stickyFooter ? 'sticky' : 'static',
            bottom: 0,
            zIndex: stickyFooter ? 1000 : 'auto',
            backgroundColor: '#FFFFFF',
            flexShrink: 0,
          }}
        >
          <TableFooter
            totalItems={totalItems}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={onPageChange}
            onItemsPerPageChange={onItemsPerPageChange}
            itemsPerPageOptions={itemsPerPageOptions}
            showItemsPerPage={showItemsPerPage}
            showTotalItems={showTotalItems}
            showPageNumbers={showPageNumbers}
            customFooterContent={customFooterContent}
            selectedCount={selectedItems.length}
          />
        </Paper>
      ) : null}
    </Box>
  )
}

// Memoized so that unrelated parent re-renders (e.g. typing in the Search
// popper, which lives in the same parent) do not re-render this large table.
export default React.memo(CommonTable)
